import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";

const SCOPE_LABEL = {
  wholesale: "Wholesale",
  retail: "Retail",
  reseller: "Reseller",
  supply_install: "Supply & Install",
};

const money = (n) =>
  typeof n === "number"
    ? "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const diffColour = (pass) =>
  pass ? "text-emerald-600" : "text-red-600 font-semibold";

export default function PricingHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/admin/pricing-health");
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="pricing-health-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#fda500] font-bold mb-1">
            Final QA · Calibration Lock
          </div>
          <h1 className="text-3xl font-bold text-[#0A1128] flex items-center gap-3">
            <Activity className="text-[#254391]" size={28} strokeWidth={1.8} />
            Pricing Health
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Live variance check against Excel benchmarks. Tolerance:{" "}
            <span className="font-mono font-semibold">± ${data?.tolerance?.toFixed(2) ?? "1.00"}</span>{" "}
            per scope.
          </p>
          {data?.hierarchy_rule && (
            <p className="text-xs text-slate-500 mt-1 font-mono">
              Hierarchy rule: {data.hierarchy_rule}
            </p>
          )}
        </div>
        <button
          onClick={load}
          data-testid="refresh-health-btn"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Overall banner */}
      {data && (
        <div
          data-testid="overall-banner"
          className={`mb-6 rounded-lg p-4 flex items-center gap-3 ${
            data.overall_pass
              ? "bg-emerald-50 border border-emerald-200"
              : "bg-red-50 border border-red-200"
          }`}
        >
          {data.overall_pass ? (
            <CheckCircle2 className="text-emerald-600" size={24} />
          ) : (
            <AlertTriangle className="text-red-600" size={24} />
          )}
          <div>
            <div
              className={`font-bold ${
                data.overall_pass ? "text-emerald-700" : "text-red-700"
              }`}
            >
              {data.overall_pass ? "ALL SYSTEMS CALIBRATED" : "CALIBRATION FAILED"}
            </div>
            <div className="text-sm text-slate-600">
              {data.overall_pass
                ? `${data.rows.length} product benchmarks within ± $${data.tolerance} variance.`
                : `Failed: ${data.failed_labels?.join(", ")}`}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 rounded-lg p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-20 text-slate-500">Loading benchmarks…</div>
      )}

      {data?.rows?.map((row) => (
        <BenchmarkCard key={row.key} row={row} />
      ))}
    </div>
  );
}

function BenchmarkCard({ row }) {
  const pending = row.calibration_pending;
  return (
    <div
      data-testid={`bench-${row.key}`}
      className="bg-white border border-slate-200 rounded-lg mb-5 overflow-hidden"
    >
      <div
        className={`px-5 py-3 border-b flex items-center justify-between ${
          row.pass ? "bg-slate-50" : "bg-red-50 border-red-200"
        }`}
      >
        <div>
          <div className="font-semibold text-[#0A1128]">{row.label}</div>
          <div className="text-xs text-slate-500 font-mono mt-0.5">
            {row.product_type} · {JSON.stringify(row.inputs)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {pending && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800">
              Pending Excel target
            </span>
          )}
          {!pending && row.pass && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700">
              PASS
            </span>
          )}
          {!pending && !row.pass && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-red-100 text-red-700">
              CALIBRATION FAILED
            </span>
          )}
        </div>
      </div>

      <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-sm bg-white">
        <Metric label="Weight" value={`${row.weight_kg ?? "—"} kg`} />
        <Metric label="Subtotal" value={money(row.subtotal)} />
        <Metric label="Additional" value={money(row.additional_costs)} />
        <Metric label="Contingency" value={money(row.contingency)} />
        <Metric label="Cost (× 1.01)" value={money(row.cost_after_contingency)} />
      </div>

      {/* Scope hierarchy summary */}
      {row.scopes && (
        <div className="px-5 pb-4">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
            Scope hierarchy (must be W &lt; Rs &lt; Rt &lt; SI)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["wholesale", "Wholesale"],
              ["reseller", "Reseller"],
              ["retail", "Retail"],
              ["supply_install", "Supply & Install"],
            ].map(([sk, label]) => (
              <div
                key={sk}
                className={`rounded border px-3 py-2 ${
                  row.hierarchy_ok ? "border-slate-200" : "border-red-300 bg-red-50"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
                <div className="font-mono text-base text-[#0A1128] mt-0.5">
                  {money(row.scopes[sk])}
                </div>
              </div>
            ))}
          </div>
          {row.hierarchy_violations?.length > 0 && (
            <div className="mt-3 px-3 py-2 rounded border border-red-300 bg-red-50 text-xs text-red-700">
              <div className="font-semibold mb-1">Hierarchy violations:</div>
              {row.hierarchy_violations.map((v) => (
                <div key={v} className="font-mono">• {v}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {!pending && (
        <div className="px-5 pb-5">
          <table className="w-full text-sm" data-testid={`variance-table-${row.key}`}>
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Scope</th>
                <th className="py-2 pr-4 text-right">Excel Expected</th>
                <th className="py-2 pr-4 text-right">App Actual</th>
                <th className="py-2 pr-4 text-right">Variance</th>
                <th className="py-2 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(row.scopes_diff || {}).map(([sk, sv]) => (
                <tr key={sk} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 pr-4 font-medium text-slate-800">
                    {SCOPE_LABEL[sk] || sk}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">{money(sv.expected)}</td>
                  <td className="py-2 pr-4 text-right font-mono">{money(sv.actual)}</td>
                  <td className={`py-2 pr-4 text-right font-mono ${diffColour(sv.pass)}`}>
                    {sv.diff >= 0 ? "+" : ""}
                    {money(sv.diff).replace("$", "$")}
                  </td>
                  <td className="py-2 text-right">
                    {sv.pass ? (
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-700">
                        OK
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-700">
                        FAILED
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pending && (
        <div className="px-5 pb-5 text-sm text-slate-600">
          <div className="mb-2">
            <span className="font-semibold">Live calculated scopes:</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(row.scopes || {}).map(([sk, v]) => (
              <div key={sk} className="rounded border border-slate-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {SCOPE_LABEL[sk] || sk}
                </div>
                <div className="font-mono text-base text-[#0A1128] mt-0.5">{money(v)}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded border border-amber-200">
            Calibration pending. Provide an Excel target price for this product and
            it will be variance-checked automatically.
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-sm text-[#0A1128] mt-0.5">{value}</div>
    </div>
  );
}
